import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  login,
  createAccount,
  resetPassword,
  forgotPassword,
  logoutUser,
  getProfile,
  updateProfile,
  uploadProfileImage,
  get2FAStatus,
  getSocialAccounts,
  verifyEmail,
  socialLogin,
  getSessions,
  revokeSession,
  revokeAllOtherSessions,
  toggleVisitorMode,
  getCookiePreferencesThunk,
  updateCookiePreferences,
  deleteUserAccount,
} from "../thunks/auth.thunk";
import {
  User,
  TwoFactorStatus,
  SocialAccount,
} from "../interfaces/auth.interface";
import type { PaginationMetadata, Session } from "../../../services/sessionService";

import { TokenManager } from "../../../utils/storage/tokenmanager.storage";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  profileLoaded: boolean;
  error: any | null;
  twoFactorStatus: TwoFactorStatus | null;
  socialAccounts: SocialAccount[];
  sessions: Session[];
  sessionsPagination: PaginationMetadata;
  isVisitor: boolean;
  showCookiePreferencesModal: boolean;
  lastTokenCheck: number; // Track when tokens were last validated
  cookiePreferences: any | null; // Store user's cookie preferences
  cookiePreferencesLoaded: boolean; // Track if preferences have been loaded
}

const initialState: AuthState = {
  user: TokenManager.getUser(),
  isAuthenticated: TokenManager.isAuthenticated(),
  isLoading: false,
  profileLoaded: false,
  error: null,
  twoFactorStatus: null,
  socialAccounts: [],
  sessions: [],
  sessionsPagination: { total: 0, page: 1, limit: 15, totalPages: 1 },
  isVisitor: TokenManager.isVisitor(),
  showCookiePreferencesModal: false,
  lastTokenCheck: Date.now(),
  cookiePreferences: null,
  cookiePreferencesLoaded: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.profileLoaded = false;
      state.error = null;
      state.isVisitor = false;
      state.twoFactorStatus = null;
      state.socialAccounts = [];
      state.lastTokenCheck = 0;
      state.cookiePreferences = null;
      state.cookiePreferencesLoaded = false;
    },
    resetErrors: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isVisitor = false;
      state.lastTokenCheck = Date.now();
    },
    setShowCookiePreferencesModal: (state, action: PayloadAction<boolean>) => {
      state.showCookiePreferencesModal = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.requires2FA && action.payload.user) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
        }
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Account
      .addCase(createAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileLoaded = true;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.lastTokenCheck = Date.now();
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.profileLoaded = true;
        state.error = action.payload;
      })
      // Update Profile
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      // Upload Profile Image
      .addCase(uploadProfileImage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 2FA Status
      .addCase(get2FAStatus.fulfilled, (state, action) => {
        state.twoFactorStatus = action.payload;
      })
      // Social Accounts
      .addCase(getSocialAccounts.fulfilled, (state, action) => {
        state.socialAccounts = action.payload;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.profileLoaded = false;
        state.error = null;
        state.twoFactorStatus = null;
        state.socialAccounts = [];
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if server logout fails, we already cleared tokens in thunk
        state.user = null;
        state.isAuthenticated = false;
        state.profileLoaded = false;
        state.twoFactorStatus = null;
        state.socialAccounts = [];
      })
      // Delete account
      .addCase(deleteUserAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUserAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.profileLoaded = false;
        state.error = null;
        state.isVisitor = false;
        state.twoFactorStatus = null;
        state.socialAccounts = [];
        state.sessions = [];
        state.cookiePreferences = null;
        state.cookiePreferencesLoaded = false;
      })
      .addCase(deleteUserAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Social Login
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Sessions
      .addCase(getSessions.fulfilled, (state, action) => {
        const payload = action.payload;
        const incoming = payload?.sessions ?? [];
        const pagination = payload?.pagination ?? initialState.sessionsPagination;
        const append = !!payload?.append;

        state.sessionsPagination = pagination;
        state.sessions = append ? [...state.sessions, ...incoming] : incoming;
      })
      .addCase(revokeSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter((s) => s.id !== action.payload);
      })
      .addCase(revokeAllOtherSessions.fulfilled, (state) => {
        state.sessions = state.sessions.filter((s) => s.isCurrent);
      })
      // Visitor Mode
      .addCase(toggleVisitorMode.fulfilled, (state, action) => {
        state.isVisitor = action.payload;
        if (action.payload) {
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      // Cookie Preferences
      .addCase(getCookiePreferencesThunk.fulfilled, (state, action) => {
        state.cookiePreferences = action.payload?.cookiePreferences || null;
        state.cookiePreferencesLoaded = true;
      })
      .addCase(getCookiePreferencesThunk.rejected, (state) => {
        state.cookiePreferencesLoaded = true; // Mark as loaded even if failed
      })
      .addCase(updateCookiePreferences.fulfilled, (state, action) => {
        state.cookiePreferences = action.payload?.cookiePreferences || null;
      });
  },
});

export const { logout, resetErrors, setUser, setShowCookiePreferencesModal } = authSlice.actions;
export default authSlice.reducer;
